import { request } from './client.js';
import type {
  CreateGenericSourceBody,
  GenericSourceList,
  GenericSourceResult,
  GenericSourceWithKey,
} from './types.js';

function base(clientTenantId: string): string {
  return `/tenants/${encodeURIComponent(clientTenantId)}/sources/generic`;
}

/** Every generic and typed (`latticeai_dlp`) source of one Client, never a key (task 129). */
export function listGenericSources(clientTenantId: string, idToken: string): Promise<GenericSourceList> {
  return request<GenericSourceList>('GET', base(clientTenantId), idToken);
}

/**
 * Create a generic source; the key is in the response and nowhere else.
 * `addService` confirms adding the covering service when the Client lacks it
 * (409 `service_required`; `manage_platform` only, task 179a).
 */
export function createGenericSource(
  clientTenantId: string,
  body: CreateGenericSourceBody,
  idToken: string,
  options: { addService?: boolean } = {},
): Promise<GenericSourceWithKey> {
  return request<GenericSourceWithKey>('POST', base(clientTenantId), idToken, {
    body,
    query: options.addService ? { add_service: 'true' } : undefined,
  });
}

/** A new key for the source; the old one stops working at once. */
export function reissueGenericKey(
  clientTenantId: string,
  sourceId: string,
  idToken: string,
): Promise<GenericSourceWithKey> {
  return request<GenericSourceWithKey>(
    'POST',
    `${base(clientTenantId)}/${encodeURIComponent(sourceId)}/key`,
    idToken,
  );
}

/** Replace a generic source's mapping (422 `invalid_mapping`; 409 `mapping_builtin` for a typed source). */
export function putGenericMapping(
  clientTenantId: string,
  sourceId: string,
  mapping: Record<string, unknown>,
  idToken: string,
): Promise<GenericSourceResult> {
  return request<GenericSourceResult>(
    'PUT',
    `${base(clientTenantId)}/${encodeURIComponent(sourceId)}/mapping`,
    idToken,
    { body: mapping },
  );
}

/** Remove a source and kill its key. Events already stored stay. */
export function removeGenericSource(
  clientTenantId: string,
  sourceId: string,
  idToken: string,
): Promise<GenericSourceResult> {
  return request<GenericSourceResult>(
    'DELETE',
    `${base(clientTenantId)}/${encodeURIComponent(sourceId)}`,
    idToken,
  );
}
